import React, { useEffect, useState, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { useClub } from '../../hooks/useClub';
import { useAuth } from '../../hooks/useAuth';
import LeadOutcomeModal from './LeadOutcomeModal';

function ScoreBar({ score }) {
  const pct = Math.min(100, Math.max(0, score || 0));
  const color = pct >= 70 ? '#ef4444' : pct >= 45 ? '#f97316' : pct >= 20 ? '#eab308' : '#6b7280';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ flex: 1, height: 4, background: 'var(--surface-3)', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 2 }} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 700, color, fontFamily: 'DM Mono, monospace', minWidth: 24 }}>{score}</span>
    </div>
  );
}

function pipelineLabel(pipeline) {
  const map = {
    'win_back_non_member':  'Win Back — Non-Member',
    'win_back_past_member': 'Win Back — Past Member',
    'lead_engine':          'Lead Engine',
    'trial_ready':          'Trial Ready',
  };
  return map[pipeline] || pipeline;
}

export default function LeadMyLeads({ onSelectProspect }) {
  const { get, post } = useApi();
  const { currentClub } = useClub();
  const { user } = useAuth();
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [search, setSearch]     = useState('');
  const [outcomeTarget, setOutcomeTarget] = useState(null);
  const [completed, setCompleted] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    get(`/lead/my-leads?club=${currentClub.id}`)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [currentClub.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" /></div>;
  if (error)   return <div style={{ padding: 24, color: 'var(--red)', textAlign: 'center' }}>Failed to load — {error}</div>;

  const completedIds = new Set(completed.map(c => c.id));
  const prospects = (data?.prospects || []).filter(p => {
    if (completedIds.has(p.id)) return false;
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return p.name?.toLowerCase().includes(s) || p.email?.toLowerCase().includes(s);
  });

  return (
    <div>
      {/* Header */}
      <div style={{
        background: 'var(--surface-2)', border: '1px solid var(--border)',
        borderRadius: 8, padding: '10px 14px', marginBottom: 16,
        fontSize: 12, color: 'var(--text-secondary)',
      }}>
        👤 <strong style={{ color: 'var(--text)' }}>My Leads</strong> — prospects assigned to you
      </div>

      {/* KPI pill */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <div style={{
          background: 'var(--surface-2)', border: '1px solid var(--border)',
          borderRadius: 7, padding: '6px 16px', textAlign: 'center',
        }}>
          <div style={{ fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>Assigned</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', fontFamily: 'DM Mono, monospace' }}>{data?.total || 0}</div>
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <input
          className="field-input"
          type="text"
          placeholder="Search by name or email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: '100%' }}
        />
      </div>

      {prospects.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">👤</div>
          <div className="empty-state-text">
            {data?.total === 0 ? 'No leads assigned to you yet.' : `No results for "${search}"`}
          </div>
        </div>
      ) : (
        prospects.map(p => (
          <div key={p.id} style={{
            background: 'var(--surface-2)', border: '1px solid var(--border)',
            borderRadius: 8, padding: '10px 14px', marginBottom: 6,
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 3, textTransform: 'capitalize' }}>
                  {p.name}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{
                    background: 'rgba(234,179,8,0.2)', color: 'var(--yellow)',
                    fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                  }}>{pipelineLabel(p.pipeline)}</span>
                  {p.skillLevel && (
                    <span style={{ fontSize: 10, color: 'var(--text-muted)', background: 'var(--surface-3)', padding: '1px 6px', borderRadius: 4 }}>
                      {p.skillLevel}
                    </span>
                  )}
                  {p.activationName && (
                    <span style={{
                      background: 'rgba(251,191,36,0.12)', color: '#f59e0b',
                      border: '1px solid rgba(251,191,36,0.3)',
                      fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                    }}>
                      ⚡ {p.activationName}
                    </span>
                  )}
                  {p.activationName && p.activationName.toLowerCase().includes('referral') && (
                    <span style={{
                      background: 'rgba(56,189,248,0.12)', color: '#38bdf8',
                      border: '1px solid rgba(56,189,248,0.3)',
                      fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                    }}>
                      🎟️ Referral
                    </span>
                  )}
                  {p.assignedToName && (
                    <span style={{
                      background: 'rgba(59,130,246,0.15)', color: '#60a5fa',
                      fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                    }}>
                      👤 {p.assignedToName}
                    </span>
                  )}
                </div>
                <ScoreBar score={p.score} />
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  style={{ background: 'var(--yellow)', color: '#000', border: 'none', borderRadius: 5, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                  onClick={() => setOutcomeTarget(p)}
                >LOG</button>
                <button
                  style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text)', borderRadius: 5, padding: '4px 10px', fontSize: 11, cursor: 'pointer' }}
                  onClick={() => onSelectProspect(p.id)}
                >VIEW</button>
              </div>
            </div>
          </div>
        ))
      )}

      {outcomeTarget && (
        <LeadOutcomeModal
          prospect={outcomeTarget}
          onClose={() => setOutcomeTarget(null)}
          onSaved={(outcome) => {
            setCompleted(prev => [...prev, { ...outcomeTarget, outcome }]);
            setOutcomeTarget(null);
            load();
          }}
        />
      )}
    </div>
  );
}
